type CropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type PipelineFn = (data: Uint8ClampedArray, gray: Uint8ClampedArray, w: number, h: number) => void;

export type PreprocessVariant = {
  label: string;
  file: File;
};

async function loadImage(file: File | Blob): Promise<{ img: HTMLImageElement; width: number; height: number }> {
  const imgUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Falha ao carregar imagem'));
      el.src = imgUrl;
    });
    return { img, width: img.width, height: img.height };
  } finally {
    URL.revokeObjectURL(imgUrl);
  }
}

function otsuThreshold(gray: Uint8ClampedArray): number {
  const hist = new Array<number>(256).fill(0);
  for (let i = 0; i < gray.length; i += 1) hist[gray[i]] += 1;

  const total = gray.length;
  let sum = 0;
  for (let t = 0; t < 256; t += 1) sum += t * hist[t];

  let sumB = 0;
  let wB = 0;
  let varMax = 0;
  let threshold = 128;

  for (let t = 0; t < 256; t += 1) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;

    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const varBetween = wB * wF * (mB - mF) * (mB - mF);

    if (varBetween > varMax) {
      varMax = varBetween;
      threshold = t;
    }
  }

  return threshold;
}

function detectPaperCrop(gray: Uint8ClampedArray, w: number, h: number): CropRect {
  const rowBright = new Array<number>(h).fill(0);
  const colBright = new Array<number>(w).fill(0);

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const v = gray[y * w + x];
      if (v >= 165) {
        rowBright[y] += 1;
        colBright[x] += 1;
      }
    }
  }

  const minRowRatio = 0.08;
  const minColRatio = 0.06;

  let top = 0;
  let bottom = h - 1;
  let left = 0;
  let right = w - 1;

  while (top < h && rowBright[top] / w < minRowRatio) top += 1;
  while (bottom > top && rowBright[bottom] / w < minRowRatio) bottom -= 1;
  while (left < w && colBright[left] / h < minColRatio) left += 1;
  while (right > left && colBright[right] / h < minColRatio) right -= 1;

  const cropW = right - left + 1;
  const cropH = bottom - top + 1;

  if (cropW < w * 0.25 || cropH < h * 0.25) {
    return { x: 0, y: 0, width: w, height: h };
  }

  const padX = Math.round(cropW * 0.02);
  const padY = Math.round(cropH * 0.02);

  return {
    x: Math.max(0, left - padX),
    y: Math.max(0, top - padY),
    width: Math.min(w - Math.max(0, left - padX), cropW + padX * 2),
    height: Math.min(h - Math.max(0, top - padY), cropH + padY * 2),
  };
}

function sharpenInPlace(data: Uint8ClampedArray, gray: Uint8ClampedArray, w: number, h: number): void {
  const copy = new Uint8ClampedArray(gray);
  const kernel = [0, -1, 0, -1, 5, -1, 0, -1, 0];

  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      let sum = 0;
      let ki = 0;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          sum += copy[(y + dy) * w + (x + dx)] * kernel[ki];
          ki += 1;
        }
      }
      const p = (y * w + x) * 4;
      const v = Math.max(0, Math.min(255, sum));
      data[p] = v;
      data[p + 1] = v;
      data[p + 2] = v;
    }
  }
}

function enhanceGrayPipeline(data: Uint8ClampedArray, gray: Uint8ClampedArray, w: number, h: number): void {
  let min = 255;
  let max = 0;
  for (let p = 0; p < gray.length; p += 1) {
    min = Math.min(min, gray[p]);
    max = Math.max(max, gray[p]);
  }

  const range = Math.max(1, max - min);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    let v = ((gray[p] - min) * 255) / range;
    v = Math.max(0, Math.min(255, v * 1.08 - 8));
    data[i] = v;
    data[i + 1] = v;
    data[i + 2] = v;
    data[i + 3] = 255;
    gray[p] = v;
  }

  sharpenInPlace(data, gray, w, h);
}

function binaryOtsuPipeline(data: Uint8ClampedArray, gray: Uint8ClampedArray, w: number, h: number): void {
  enhanceGrayPipeline(data, gray, w, h);

  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    gray[p] = data[i];
  }

  const thr = otsuThreshold(gray);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const v = gray[p] < thr ? 0 : 255;
    data[i] = v;
    data[i + 1] = v;
    data[i + 2] = v;
    data[i + 3] = 255;
  }
}

function binaryAdaptivePipeline(data: Uint8ClampedArray, gray: Uint8ClampedArray, w: number, h: number): void {
  enhanceGrayPipeline(data, gray, w, h);

  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    gray[p] = data[i];
  }

  const blockSize = Math.max(24, Math.round(Math.min(w, h) / 40));
  const thresholds = new Float32Array(Math.ceil(w / blockSize) * Math.ceil(h / blockSize));

  for (let by = 0, bi = 0; by < h; by += blockSize) {
    for (let bx = 0; bx < w; bx += blockSize, bi += 1) {
      let sum = 0;
      let count = 0;
      const yMax = Math.min(h, by + blockSize);
      const xMax = Math.min(w, bx + blockSize);
      for (let y = by; y < yMax; y += 1) {
        for (let x = bx; x < xMax; x += 1) {
          sum += gray[y * w + x];
          count += 1;
        }
      }
      thresholds[bi] = count ? sum / count - 10 : 128;
    }
  }

  for (let y = 0; y < h; y += 1) {
    const by = Math.floor(y / blockSize);
    for (let x = 0; x < w; x += 1) {
      const bx = Math.floor(x / blockSize);
      const bi = by * Math.ceil(w / blockSize) + bx;
      const p = y * w + x;
      const v = gray[p] < thresholds[bi] ? 0 : 255;
      const i = p * 4;
      data[i] = v;
      data[i + 1] = v;
      data[i + 2] = v;
      data[i + 3] = 255;
    }
  }
}

function renderProcessed(
  img: HTMLImageElement,
  crop: CropRect,
  targetLongest: number,
  pipeline: PipelineFn,
): HTMLCanvasElement {
  const cropW = crop.width;
  const cropH = crop.height;
  const longest = Math.max(cropW, cropH);
  const scale = targetLongest / longest;
  const w = Math.max(1, Math.round(cropW * scale));
  const h = Math.max(1, Math.round(cropH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas indisponível');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, crop.x, crop.y, cropW, cropH, 0, 0, w, h);

  const imageData = ctx.getImageData(0, 0, w, h);
  const { data } = imageData;
  const gray = new Uint8ClampedArray(w * h);

  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    gray[p] = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
  }

  pipeline(data, gray, w, h);
  ctx.putImageData(imageData, 0, 0);

  return canvas;
}

async function canvasToFile(canvas: HTMLCanvasElement, baseName: string, label: string): Promise<File> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Falha ao gerar imagem'))), 'image/png', 1);
  });
  return new File([blob], `${baseName}-${label}.png`, { type: 'image/png' });
}

/**
 * Gera variantes otimizadas para cupom térmico (crop + upscale + filtros).
 */
export async function buildPreprocessVariants(file: File | Blob): Promise<PreprocessVariant[]> {
  const { img, width, height } = await loadImage(file);
  const baseName = file instanceof File ? file.name.replace(/\.\w+$/, '') : 'cupom';

  const probe = document.createElement('canvas');
  probe.width = width;
  probe.height = height;
  const probeCtx = probe.getContext('2d', { willReadFrequently: true });
  if (!probeCtx) throw new Error('Canvas indisponível');
  probeCtx.drawImage(img, 0, 0);

  const probeData = probeCtx.getImageData(0, 0, width, height).data;
  const probeGray = new Uint8ClampedArray(width * height);
  for (let i = 0, p = 0; i < probeData.length; i += 4, p += 1) {
    probeGray[p] = Math.round(0.299 * probeData[i] + 0.587 * probeData[i + 1] + 0.114 * probeData[i + 2]);
  }

  const crop = detectPaperCrop(probeGray, width, height);
  const targetLongest = Math.min(3200, Math.max(2400, Math.max(crop.width, crop.height) * 2));

  const pipelines: Array<{ label: string; fn: PipelineFn }> = [
    { label: 'gray_sharp', fn: enhanceGrayPipeline },
    { label: 'binary_otsu', fn: binaryOtsuPipeline },
    { label: 'binary_adaptive', fn: binaryAdaptivePipeline },
  ];

  const variants: PreprocessVariant[] = [];
  for (const pipeline of pipelines) {
    const canvas = renderProcessed(img, crop, targetLongest, pipeline.fn);
    variants.push({
      label: pipeline.label,
      file: await canvasToFile(canvas, baseName, pipeline.label),
    });
  }

  return variants;
}
